import React from 'react';
import {
	Menu,
	MenuItem,
	ListItemText,
	ListSubheader,
	Stack,
	Switch,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAdminModeToggle, setThemeMode } from '@/features/userPrefs/userPrefsSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { UserAvatar } from '@/components/UserAvatar';
import { IS_DEV, IS_PRE } from '@/utils/environment';
import { DarkModeSwitch } from '@/components/buttons/DarkModeSwitch';
import { useNavVariant } from '@/components/layouts/navVariants';
import { paths } from '@/app/paths';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';
import { useAdminModeContext } from '@/context/adminModeContext';

export const UserMenu = ({ anchorEl, onClose, onSignOut }) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const isDarkMode = useSelector((state) => state.userPrefs.themeMode === 'dark');
	const handleToggleDarkMode = () => dispatch(setThemeMode(isDarkMode ? 'light' : 'dark'));

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { MobileActions, ProfileMenuItem } = useNavVariant();
	const { canAccessAdminMode, isAdminModeOn } = useAdminModeAccess();
	const { isAdminMode } = useAdminModeContext();
	const navigate = useNavigate();
	const handleToggleAdminMode = () => {
		dispatch(setAdminModeToggle(!isAdminModeOn));
		// Admin mode opens on the users list, the page admins use most
		navigate(isAdminModeOn ? paths.home : `${paths.admin}/users`);
		onClose();
	};

	return (
		<Menu
			id='menu-appbar'
			anchorEl={anchorEl}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			keepMounted
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			open={Boolean(anchorEl)}
			onClose={onClose}
			{...((IS_DEV || IS_PRE) && {
				sx: {
					'& .MuiMenu-list': {
						pb: 0,
					},
				},
			})}
		>
			{!isAdminMode && (
				<ListSubheader sx={{ lineHeight: '36px', backgroundColor: 'transparent' }}>
					<Stack direction='row' width='100%' spacing={1} alignItems={'center'}>
						<UserAvatar userId={userDetails?.userId} />
						<Typography sx={{ fontSize: '12px' }}>{email}</Typography>
					</Stack>
				</ListSubheader>
			)}
			{canAccessAdminMode && (
				<MenuItem onClick={handleToggleAdminMode} sx={{ justifyContent: 'space-between' }}>
					<ListItemText>Admin mode</ListItemText>
					<Switch checked={isAdminModeOn} size='small' />
				</MenuItem>
			)}
			{isMobile && MobileActions && <MobileActions />}
			{ProfileMenuItem && <ProfileMenuItem onClose={onClose} />}
			<MenuItem onClick={handleToggleDarkMode} sx={{ justifyContent: 'space-between' }}>
				<ListItemText>{isDarkMode ? 'Dark mode on' : 'Light mode on'}</ListItemText>
				<DarkModeSwitch />
			</MenuItem>
			<MenuItem onClick={onSignOut}>Sign out</MenuItem>
			{(IS_DEV || IS_PRE) && (
				<ListSubheader
					sx={{
						backgroundColor: IS_DEV
							? theme.palette.primary.main
							: theme.palette.secondary.main,
						py: 0.5,
					}}
				>
					<Typography
						sx={{
							fontSize: '10px',
							color: theme.palette.getContrastText(
								IS_DEV ? theme.palette.primary.main : theme.palette.secondary.main,
							),
						}}
					>
						{IS_DEV ? 'DEV' : 'PRE'}
					</Typography>
				</ListSubheader>
			)}
		</Menu>
	);
};
